import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader, AlertCircle, Package, Search } from 'lucide-react';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { useDebounce } from '../../hooks/useDebounce';
import { buildTeamApiService } from '../../services/buildTeamApi';
import {
  BuildItem,
  BuildItemStatus,
  BuildProject,
  BuildTag,
  BUILD_ITEM_STATUSES,
} from '../../types/buildTeam';
import { cn } from '../../utils/cn';
import { ApogeePackSearchField } from '../apogee/ApogeePackSearchField';

export interface EditBuildItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  item: BuildItem;
}

function formatStatusLabel(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function parseDimensions(json: string | null): { x: string; y: string; z: string } {
  if (!json) return { x: '', y: '', z: '' };
  try {
    const o = JSON.parse(json) as Record<string, unknown>;
    return {
      x: o.x != null ? String(o.x) : '',
      y: o.y != null ? String(o.y) : '',
      z: o.z != null ? String(o.z) : '',
    };
  } catch {
    return { x: '', y: '', z: '' };
  }
}

function parseCoordinates(json: string | null): { world: string; x: string; y: string; z: string } {
  if (!json) return { world: '', x: '', y: '', z: '' };
  try {
    const o = JSON.parse(json) as Record<string, unknown>;
    return {
      world: o.world != null ? String(o.world) : '',
      x: o.x != null ? String(o.x) : '',
      y: o.y != null ? String(o.y) : '',
      z: o.z != null ? String(o.z) : '',
    };
  } catch {
    return { world: '', x: '', y: '', z: '' };
  }
}

function buildDimensionsJson(x: string, y: string, z: string): string | null | undefined {
  const o: Record<string, number> = {};
  if (x.trim() !== '') o.x = Number(x);
  if (y.trim() !== '') o.y = Number(y);
  if (z.trim() !== '') o.z = Number(z);
  if (x.trim() === '' && y.trim() === '' && z.trim() === '') return null;
  return Object.keys(o).length > 0 ? JSON.stringify(o) : null;
}

function buildCoordinatesJson(world: string, x: string, y: string, z: string): string | null | undefined {
  const o: Record<string, string | number> = {};
  if (world.trim() !== '') o.world = world.trim();
  if (x.trim() !== '') o.x = Number(x);
  if (y.trim() !== '') o.y = Number(y);
  if (z.trim() !== '') o.z = Number(z);
  if (
    world.trim() === '' &&
    x.trim() === '' &&
    y.trim() === '' &&
    z.trim() === ''
  ) {
    return null;
  }
  return Object.keys(o).length > 0 ? JSON.stringify(o) : null;
}

const LABEL_CLASS = 'text-xs font-medium text-glass-400 mb-1.5 block';

export function EditBuildItemModal({ isOpen, onClose, onSuccess, item }: EditBuildItemModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(modalRef, onClose);

  const [title, setTitle] = useState(item.title);
  const [description, setDescription] = useState(item.description);
  const [selectedProject, setSelectedProject] = useState<BuildProject | null>(null);
  const [projectSearch, setProjectSearch] = useState('');
  const debouncedProjectSearch = useDebounce(projectSearch, 300);
  const [projectResults, setProjectResults] = useState<BuildProject[]>([]);
  const [isSearchingProjects, setIsSearchingProjects] = useState(false);
  const [status, setStatus] = useState<BuildItemStatus>(item.status);
  const [priority, setPriority] = useState(item.priority);
  const [category, setCategory] = useState(item.category ?? '');
  const [dimX, setDimX] = useState('');
  const [dimY, setDimY] = useState('');
  const [dimZ, setDimZ] = useState('');
  const [coordWorld, setCoordWorld] = useState('');
  const [coordX, setCoordX] = useState('');
  const [coordY, setCoordY] = useState('');
  const [coordZ, setCoordZ] = useState('');
  const [tags, setTags] = useState<BuildTag[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [initialTagIds, setInitialTagIds] = useState<string[]>([]);
  const [isLoadingTags, setIsLoadingTags] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modpackApogeeId, setModpackApogeeId] = useState<number | null>(item.apogee_id ?? null);

  useEffect(() => {
    if (!isOpen) return;

    const dims = parseDimensions(item.dimensions);
    const coords = parseCoordinates(item.coordinates);
    setTitle(item.title);
    setDescription(item.description);
    setStatus(item.status);
    setPriority(item.priority);
    setCategory(item.category ?? '');
    setDimX(dims.x);
    setDimY(dims.y);
    setDimZ(dims.z);
    setCoordWorld(coords.world);
    setCoordX(coords.x);
    setCoordY(coords.y);
    setCoordZ(coords.z);
    setModpackApogeeId(item.apogee_id ?? null);
    setSelectedProject(null);
    setProjectSearch('');
    setProjectResults([]);
    setError(null);

    const loadProject = item.project_id
      ? buildTeamApiService.getProjectById(item.project_id).then((p) =>
          setSelectedProject({
            id: p.id,
            title: p.title,
            description: p.description,
            status: p.status,
            priority: p.priority,
            category: p.category,
            created_at: p.created_at,
            created_by: p.created_by,
            updated_at: p.updated_at,
            updated_by: p.updated_by,
            started_at: p.started_at,
            due_date: p.due_date,
            completed_at: p.completed_at,
            metadata: p.metadata,
            apogee_id: p.apogee_id,
          })
        )
      : Promise.resolve();

    setIsLoadingTags(true);
    Promise.all([
      loadProject,
      buildTeamApiService.listTags(),
      buildTeamApiService.getBuildTags(item.id),
    ])
      .then(([, allTags, buildTags]) => {
        setTags(allTags);
        const ids = buildTags.map((t) => t.id);
        setSelectedTagIds(ids);
        setInitialTagIds(ids);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load tags or project');
      })
      .finally(() => setIsLoadingTags(false));
  }, [isOpen, item]);

  useEffect(() => {
    if (!debouncedProjectSearch || debouncedProjectSearch.length < 1) {
      setProjectResults([]);
      return;
    }
    setIsSearchingProjects(true);
    buildTeamApiService
      .searchProjects(debouncedProjectSearch)
      .then(setProjectResults)
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to search projects');
      })
      .finally(() => setIsSearchingProjects(false));
  }, [debouncedProjectSearch]);

  const handleToggleTag = (tagId: string) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    if (!description.trim()) {
      setError('Description is required');
      return;
    }

    const dimensions = buildDimensionsJson(dimX, dimY, dimZ);
    const coordinates = buildCoordinatesJson(coordWorld, coordX, coordY, coordZ);

    setIsSubmitting(true);
    try {
      await buildTeamApiService.updateBuildItem(item.id, {
        title: title.trim(),
        description: description.trim(),
        project_id: selectedProject?.id ?? null,
        status,
        priority,
        category: category.trim() || null,
        dimensions: dimensions === undefined ? undefined : dimensions,
        coordinates: coordinates === undefined ? undefined : coordinates,
        apogee_id: modpackApogeeId,
      });

      const toAdd = selectedTagIds.filter((id) => !initialTagIds.includes(id));
      const toRemove = initialTagIds.filter((id) => !selectedTagIds.includes(id));
      await Promise.all([
        ...toAdd.map((id) => buildTeamApiService.tagBuild(item.id, id)),
        ...toRemove.map((id) => buildTeamApiService.untagBuild(item.id, id)),
      ]);

      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update build item');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay z-[9999]">
      <div
        ref={modalRef}
        className="modal-panel max-w-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-build-title"
      >
        <div className="modal-header">
          <h2 id="edit-build-title" className="modal-title flex items-center gap-2">
            <Package className="w-5 h-5 text-accent-300" />
            Edit build item
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="modal-close touch-manipulation"
            aria-label="Close"
            title="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {error && (
          <div className="alert alert-danger mb-4">
            <AlertCircle />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={LABEL_CLASS}>Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="glass-input w-full"
              required
            />
          </div>

          <div>
            <label className={LABEL_CLASS}>Description *</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="glass-input w-full"
              rows={3}
              required
            />
          </div>

          <ApogeePackSearchField
            value={modpackApogeeId}
            onChange={setModpackApogeeId}
            compact
            description="Optional. Links this build to a pack in Apogee. Clear to remove."
          />

          <div>
            <label className={LABEL_CLASS}>Project</label>
            {selectedProject ? (
              <div className="flex items-center justify-between p-2 pl-3 rounded-lg bg-accent-500/10 border border-accent-500/20 gap-2">
                <span className="text-sm font-medium text-white truncate">{selectedProject.title}</span>
                <button
                  type="button"
                  onClick={() => setSelectedProject(null)}
                  className="btn-icon btn-icon-sm"
                  aria-label="Clear project"
                  title="Clear project"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400 pointer-events-none" />
                <input
                  type="text"
                  value={projectSearch}
                  onChange={(e) => setProjectSearch(e.target.value)}
                  className="glass-input w-full pl-9"
                  placeholder="Search projects…"
                />
                {(projectResults.length > 0 || isSearchingProjects) && projectSearch.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 glass-card overflow-hidden z-10 max-h-48 overflow-y-auto divide-y divide-glass-200/10">
                    {isSearchingProjects && (
                      <div className="p-3 flex items-center justify-center">
                        <Loader className="w-4 h-4 animate-spin text-accent-400" />
                      </div>
                    )}
                    {projectResults.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => {
                          setSelectedProject(p);
                          setProjectSearch('');
                          setProjectResults([]);
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-glass-200/5 text-sm text-white"
                      >
                        {p.title}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={LABEL_CLASS}>Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as BuildItemStatus)}
                className="glass-select w-full"
              >
                {BUILD_ITEM_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {formatStatusLabel(s)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={LABEL_CLASS}>Priority (1–10)</label>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={1}
                  max={10}
                  value={priority}
                  onChange={(e) => setPriority(Number(e.target.value))}
                  className="flex-1 h-2 rounded-full appearance-none bg-glass-200/30 accent-accent-500"
                />
                <input
                  type="number"
                  min={1}
                  max={10}
                  value={priority}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    if (v >= 1 && v <= 10) setPriority(v);
                  }}
                  className="glass-input w-16 text-center tabular-nums"
                />
              </div>
            </div>
          </div>

          <div>
            <label className={LABEL_CLASS}>Category</label>
            <input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="glass-input w-full"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={LABEL_CLASS}>Dimensions (x, y, z)</label>
              <div className="grid grid-cols-3 gap-2">
                <input
                  type="number"
                  value={dimX}
                  onChange={(e) => setDimX(e.target.value)}
                  className="glass-input w-full tabular-nums"
                  placeholder="X"
                  aria-label="Dimension X"
                />
                <input
                  type="number"
                  value={dimY}
                  onChange={(e) => setDimY(e.target.value)}
                  className="glass-input w-full tabular-nums"
                  placeholder="Y"
                  aria-label="Dimension Y"
                />
                <input
                  type="number"
                  value={dimZ}
                  onChange={(e) => setDimZ(e.target.value)}
                  className="glass-input w-full tabular-nums"
                  placeholder="Z"
                  aria-label="Dimension Z"
                />
              </div>
            </div>

            <div>
              <label className={LABEL_CLASS}>Coordinates</label>
              <input
                type="text"
                value={coordWorld}
                onChange={(e) => setCoordWorld(e.target.value)}
                className="glass-input w-full mb-2"
                placeholder="World name"
                aria-label="World name"
              />
              <div className="grid grid-cols-3 gap-2">
                <input
                  type="number"
                  value={coordX}
                  onChange={(e) => setCoordX(e.target.value)}
                  className="glass-input w-full tabular-nums"
                  placeholder="X"
                  aria-label="Coordinate X"
                />
                <input
                  type="number"
                  value={coordY}
                  onChange={(e) => setCoordY(e.target.value)}
                  className="glass-input w-full tabular-nums"
                  placeholder="Y"
                  aria-label="Coordinate Y"
                />
                <input
                  type="number"
                  value={coordZ}
                  onChange={(e) => setCoordZ(e.target.value)}
                  className="glass-input w-full tabular-nums"
                  placeholder="Z"
                  aria-label="Coordinate Z"
                />
              </div>
            </div>
          </div>

          <div>
            <label className={LABEL_CLASS}>Tags</label>
            {isLoadingTags ? (
              <div className="p-3 flex items-center justify-center">
                <Loader className="w-4 h-4 animate-spin text-accent-400" />
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => handleToggleTag(tag.id)}
                    className={cn(
                      'chip transition-colors',
                      selectedTagIds.includes(tag.id)
                        ? 'text-white'
                        : 'hover:text-white hover:border-glass-200/25'
                    )}
                    aria-pressed={selectedTagIds.includes(tag.id)}
                    style={
                      selectedTagIds.includes(tag.id)
                        ? { borderColor: tag.color, backgroundColor: `${tag.color}20` }
                        : undefined
                    }
                  >
                    {tag.name}
                  </button>
                ))}
                {tags.length === 0 && (
                  <span className="text-xs text-glass-400">No tags available</span>
                )}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="glass-button-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? <Loader className="w-4 h-4 animate-spin" /> : null}
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
